import React, { useMemo, useRef, useState, useCallback } from "react";
import { gradientColor, GRADIENT_BANDS } from "../lib/gradient";

/**
 * ElevationChart — inline-SVG elevation profile with:
 *   • Line coloured per segment by signed gradient (green/yellow/orange/red).
 *   • Draggable scrubber. Touch-friendly: pointer events, 44-px tap area.
 *   • Readout above the chart: height / distance / gradient (number + %).
 *   • Labels on highest and lowest points.
 *   • A legend chip-row sits under the chart.
 *
 * Props:
 *   points        — [[distance_km, elevation_m], …]  (already smoothed + downsampled server-side)
 *   onScrub({idx, lat, lng, elevation, distance_km, gradient}) — fires on every drag update
 *   routeLatLng   — [[lat, lng], …] parallel-length-ish; used ONLY to feed back a marker
 *                   position for the host map. The chart itself doesn't render the map.
 *
 * The chart is responsive via `preserveAspectRatio="none"` on an inner
 * viewBox-based group, so a 390-px screen renders exactly the same curve
 * shape as desktop — just scaled.
 */

const PAD_L = 36;
const PAD_R = 10;
const PAD_T = 14;
const PAD_B = 26;
// viewBox ratio ≈ 2:1 so text stays legible at mobile widths. We use
// `preserveAspectRatio` with uniform scaling (default `meet`) so axis
// labels keep their natural proportions rather than getting squished
// horizontally when the chart narrows to ~360px on a 390-px screen.
const VBOX_W = 400;
const VBOX_H = 180;

function fmtKm(km) {
  if (km == null) return "—";
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(2)} km`;
}

export default function ElevationChart({ points, routeLatLng = [], onScrub, totalDistanceKm }) {
  const [scrubIdx, setScrubIdx] = useState(0);
  const svgRef = useRef(null);
  const n = points?.length || 0;

  const geom = useMemo(() => {
    if (n < 2) return null;
    const xs = points.map((p) => p[0]);
    const ys = points.map((p) => p[1]);
    const minX = xs[0];
    const maxX = xs[xs.length - 1];
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const spanY = Math.max(1, maxY - minY);
    const spanX = Math.max(0.001, maxX - minX);
    const scaleX = (x) => PAD_L + ((x - minX) / spanX) * (VBOX_W - PAD_L - PAD_R);
    const scaleY = (y) => VBOX_H - PAD_B - ((y - minY) / spanY) * (VBOX_H - PAD_T - PAD_B);
    // Segment colours — gradient between consecutive points.
    const segments = [];
    for (let i = 1; i < n; i++) {
      const dKm = xs[i] - xs[i - 1];
      const dM = ys[i] - ys[i - 1];
      const grad = dKm > 0 ? dM / (dKm * 1000) : 0;
      segments.push({
        x1: scaleX(xs[i - 1]),
        y1: scaleY(ys[i - 1]),
        x2: scaleX(xs[i]),
        y2: scaleY(ys[i]),
        color: gradientColor(grad),
        gradient: grad,
      });
    }
    // Find highest / lowest indexes (first occurrence).
    const hiIdx = ys.indexOf(maxY);
    const loIdx = ys.indexOf(minY);
    return { xs, ys, minX, maxX, minY, maxY, scaleX, scaleY, segments, hiIdx, loIdx };
  }, [points, n]);

  const scrubFromClientX = useCallback(
    (clientX) => {
      if (!geom || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      // Map ratio across the inner plot area, not the full SVG width.
      const inner = (VBOX_W - PAD_L - PAD_R) / VBOX_W;
      const leftRatio = PAD_L / VBOX_W;
      const normalized = Math.min(1, Math.max(0, (ratio - leftRatio) / inner));
      const idx = Math.min(n - 1, Math.max(0, Math.round(normalized * (n - 1))));
      setScrubIdx(idx);
      if (onScrub && routeLatLng.length) {
        // Map elevation-point index → polyline index. The server downsamples
        // elevation to ~200 points while the polyline keeps its native
        // resolution — so interpolate by the ratio to find a route index.
        const routeIdx = Math.min(
          routeLatLng.length - 1,
          Math.max(0, Math.round((idx / (n - 1)) * (routeLatLng.length - 1))),
        );
        const [lat, lng] = routeLatLng[routeIdx] || [];
        onScrub({
          idx,
          routeIdx,
          lat,
          lng,
          distance_km: geom.xs[idx],
          elevation: geom.ys[idx],
          gradient:
            idx > 0
              ? (geom.ys[idx] - geom.ys[idx - 1]) /
                (Math.max(0.0001, (geom.xs[idx] - geom.xs[idx - 1])) * 1000)
              : 0,
        });
      }
    },
    [geom, n, onScrub, routeLatLng],
  );

  if (!geom) return null;

  const pointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    scrubFromClientX(e.clientX);
  };
  const pointerMove = (e) => {
    if (e.buttons !== 1 && e.pointerType === "mouse") return;
    scrubFromClientX(e.clientX);
  };

  const curX = geom.scaleX(geom.xs[scrubIdx]);
  const curY = geom.scaleY(geom.ys[scrubIdx]);
  const curGrad =
    scrubIdx > 0
      ? ((geom.ys[scrubIdx] - geom.ys[scrubIdx - 1]) /
          (Math.max(0.0001, geom.xs[scrubIdx] - geom.xs[scrubIdx - 1]) * 1000)) * 100
      : 0;

  // 4 even Y-axis ticks (metres).
  const yTicks = [geom.minY, geom.minY + (geom.maxY - geom.minY) / 3, geom.minY + (2 * (geom.maxY - geom.minY)) / 3, geom.maxY];

  return (
    <div className="w-full" data-testid="elevation-chart">
      {/* Readout */}
      <div className="flex items-end justify-between mb-2 gap-3 text-sm">
        <div data-testid="elev-readout-height">
          <div className="text-xs text-brand-subtle uppercase tracking-wider">Height</div>
          <div className="font-heading text-xl text-brand-text">{Math.round(geom.ys[scrubIdx])} m</div>
        </div>
        <div data-testid="elev-readout-distance">
          <div className="text-xs text-brand-subtle uppercase tracking-wider">Distance</div>
          <div className="font-heading text-xl text-brand-text">{fmtKm(geom.xs[scrubIdx])}</div>
        </div>
        <div data-testid="elev-readout-gradient">
          <div className="text-xs text-brand-subtle uppercase tracking-wider">Gradient</div>
          <div
            className="font-heading text-xl font-semibold"
            style={{ color: gradientColor(curGrad / 100) }}
          >
            {curGrad >= 0 ? "+" : ""}
            {curGrad.toFixed(1)}%
          </div>
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${VBOX_W} ${VBOX_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full touch-none cursor-ew-resize select-none"
        style={{ height: 180 }}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        data-testid="elevation-chart-svg"
      >
        {/* Y-axis guide lines + labels */}
        {yTicks.map((t, i) => (
          <g key={`yt-${i}`}>
            <line
              x1={PAD_L}
              x2={VBOX_W - PAD_R}
              y1={geom.scaleY(t)}
              y2={geom.scaleY(t)}
              stroke="rgba(0,0,0,0.08)"
              strokeWidth={1}
            />
            <text
              x={PAD_L - 6}
              y={geom.scaleY(t) + 3}
              textAnchor="end"
              fontSize={11}
              fontWeight={600}
              fill="rgba(44,58,46,0.9)"
              fontFamily="DM Sans, sans-serif"
            >
              {Math.round(t)}
            </text>
          </g>
        ))}

        {/* Area under curve — subtle sand */}
        <path
          d={
            `M ${geom.scaleX(geom.xs[0])} ${VBOX_H - PAD_B} ` +
            geom.xs
              .map((x, i) => `L ${geom.scaleX(x)} ${geom.scaleY(geom.ys[i])}`)
              .join(" ") +
            ` L ${geom.scaleX(geom.xs[n - 1])} ${VBOX_H - PAD_B} Z`
          }
          fill="rgba(246,239,224,0.6)"
          stroke="none"
        />

        {/* Coloured segment lines */}
        {geom.segments.map((s, i) => (
          <line
            key={i}
            x1={s.x1}
            y1={s.y1}
            x2={s.x2}
            y2={s.y2}
            stroke={s.color}
            strokeWidth={2.5}
            strokeLinecap="round"
          />
        ))}

        {/* Highest / lowest labels — anchor flips to `end` when the dot
            is near the right edge and to `start` when near the left, so
            the label text always stays inside the plot area. */}
        {[geom.hiIdx, geom.loIdx].map((idx, i) => {
          const x = geom.scaleX(geom.xs[idx]);
          const y = geom.scaleY(geom.ys[idx]);
          const isHi = i === 0;
          const nearRight = x > VBOX_W - PAD_R - 50;
          const nearLeft = x < PAD_L + 50;
          const anchor = nearRight ? "end" : nearLeft ? "start" : "middle";
          const dx = nearRight ? -6 : nearLeft ? 6 : 0;
          return (
            <g key={`extreme-${i}`}>
              <circle cx={x} cy={y} r={3} fill={isHi ? "#C04A2A" : "#4A8B5A"} stroke="#fff" strokeWidth={1} />
              <text
                x={x + dx}
                y={isHi ? y - 6 : y + 14}
                textAnchor={anchor}
                fontSize={11}
                fontWeight={700}
                fill="rgba(44,58,46,0.95)"
                fontFamily="DM Sans, sans-serif"
              >
                {isHi ? `▲ ${Math.round(geom.ys[idx])}m` : `▼ ${Math.round(geom.ys[idx])}m`}
              </text>
            </g>
          );
        })}

        {/* Scrubber */}
        <line
          x1={curX}
          x2={curX}
          y1={PAD_T}
          y2={VBOX_H - PAD_B}
          stroke="#2C3A2E"
          strokeWidth={1.5}
          strokeDasharray="3 3"
          data-testid="elev-scrubber-line"
        />
        <circle
          cx={curX}
          cy={curY}
          r={7}
          fill="#F6EFE0"
          stroke="#2C3A2E"
          strokeWidth={2}
          data-testid="elev-scrubber-handle"
        />

        {/* X-axis range label */}
        <text
          x={PAD_L}
          y={VBOX_H - 6}
          fontSize={11}
          fontWeight={600}
          fill="rgba(44,58,46,0.9)"
          fontFamily="DM Sans, sans-serif"
        >
          0 km
        </text>
        <text
          x={VBOX_W - PAD_R}
          y={VBOX_H - 6}
          textAnchor="end"
          fontSize={11}
          fontWeight={600}
          fill="rgba(44,58,46,0.9)"
          fontFamily="DM Sans, sans-serif"
        >
          {fmtKm(totalDistanceKm ?? geom.maxX)}
        </text>
      </svg>

      {/* Legend */}
      <div className="mt-2 flex items-center gap-3 text-xs text-brand-subtle flex-wrap" data-testid="gradient-legend">
        {GRADIENT_BANDS.map((b) => (
          <span key={b.label} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block"
              style={{ width: 14, height: 3, background: b.color, borderRadius: 2 }}
            />
            {b.label}
          </span>
        ))}
      </div>
    </div>
  );
}
